/**
 * Agent Autonomy settings — what the user's agent may do without asking.
 *
 * Layout (docs/claude/AGENT_AUTONOMY.md § Settings UI): one card — Active
 * header → level stepper + capability ladder (how much) → coverage chips with
 * a Customize disclosure (where) — followed by the membership-level "outside
 * your pipelines" toggle as its own Suggests group (a different backend and a
 * different concept).
 *
 * The autonomy card is one state machine (`useAgentAutonomyView().status`)
 * switched exhaustively; the chrome (heading, Suggests group) renders in
 * every state.
 */

import { useCallback } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useProfile } from '@/hooks/profile/useProfile';
import { useCanWrite } from '@/hooks/usePermissions';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { Toggle } from '@/lib/base/forms/toggle';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { useViewHealthMarker } from '@/lib/view-health';

import { useUser } from '../../../../allauth_auth';
import {
  ABSENT_DESCRIPTION,
  ABSENT_TITLE,
  ERROR_DESCRIPTION,
  ERROR_TITLE,
  LOADING_LABEL,
  NON_PIPELINE_BLOCKED_DESCRIPTION,
  NON_PIPELINE_DESCRIPTION,
  NON_PIPELINE_LABEL,
  NON_PIPELINE_PRIVACY,
  NON_PIPELINE_SAVE_ERROR_TITLE,
  RETRY_LABEL,
  SECTION_DESCRIPTION,
  SECTION_TITLE,
  SUGGESTS_HEADING,
} from './agent-autonomy-copy';
import { AutonomyCard } from './components/AutonomyCard';
import type { AgentAutonomyView } from './hooks/useAgentAutonomyView';
import { useAgentAutonomyView } from './hooks/useAgentAutonomyView';

function assertNever(status: never): never {
  throw new Error(`Unhandled agent autonomy status: ${String(status)}`);
}

interface AutonomyStatusProps {
  view: AgentAutonomyView;
  canWrite: boolean;
  orgId: number | undefined;
}

function AutonomyReady({ view, canWrite, orgId }: AutonomyStatusProps) {
  // One query for every pipeline in the org; terminal stage names are
  // selected per case type inside the tune panel rather than per row.
  const { pipelines } = usePipelines(orgId);
  return (
    <AutonomyCard
      enabled={view.enabled}
      level={view.displayedLevel}
      derived={view.derived}
      grants={view.grants}
      caseTypes={view.caseTypes}
      pipelines={pipelines}
      isSaving={view.isSaving}
      canWrite={canWrite}
      onActive={view.setActive}
      onLevel={view.setLevel}
      onReset={view.resetToLevel}
      onCapability={view.setCapability}
      onContactsScope={view.setContactsScope}
      onPipelineScope={view.setPipelineScope}
      onContactsVerb={view.setContactsVerb}
      onPipelineVerb={view.setPipelineVerb}
    />
  );
}

function AutonomyStatus({ view, canWrite, orgId }: AutonomyStatusProps) {
  switch (view.status) {
    case 'loading':
      return <p className='text-sm text-fg-tertiary'>{LOADING_LABEL}</p>;
    case 'absent':
      return (
        <div>
          <AlertFloating
            color='error'
            title={ABSENT_TITLE}
            description={ABSENT_DESCRIPTION}
          />
          <div className='mt-2'>
            <Button onClick={view.retry} color='secondary' size='sm'>
              {RETRY_LABEL}
            </Button>
          </div>
        </div>
      );
    case 'error':
      return (
        <div>
          <AlertFloating
            color='error'
            title={ERROR_TITLE}
            description={ERROR_DESCRIPTION}
          />
          <div className='mt-2'>
            <Button onClick={view.retry} color='secondary' size='sm'>
              {RETRY_LABEL}
            </Button>
          </div>
        </div>
      );
    case 'ready':
      return <AutonomyReady view={view} canWrite={canWrite} orgId={orgId} />;
    default:
      return assertNever(view.status);
  }
}

export function AgentAutonomySection() {
  const { currentOrg } = useOrg();
  const user = useUser();
  useViewHealthMarker('settings-section-ready');
  const canWrite = useCanWrite();
  const view = useAgentAutonomyView();
  const {
    formData,
    membershipId,
    membershipLoading,
    membershipLoadError,
    nonPipelineActionsSaving,
    nonPipelineActionsError,
    setNonPipelineActions,
  } = useProfile({ orgId: currentOrg?.id, userId: user?.id });

  const handleNonPipelineActionsToggle = useCallback(
    (checked: boolean) => {
      // Non-inverting: on = the user opts in to suggestions beyond their
      // pipelines. Error handling is managed by useProfile.
      setNonPipelineActions(checked).catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setNonPipelineActions]
  );

  return (
    <div className='space-y-8' data-component='agent-autonomy-section'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>
          {SECTION_TITLE}
        </h2>
        <p className='mt-0.5 text-sm text-fg-tertiary'>{SECTION_DESCRIPTION}</p>
      </div>

      <AutonomyStatus view={view} canWrite={canWrite} orgId={currentOrg?.id} />

      <div>
        <h3 className='text-sm font-semibold text-fg-primary'>
          {SUGGESTS_HEADING}
        </h3>
        {membershipLoadError && (
          <div className='mt-2 mb-2'>
            <AlertFloating
              color='error'
              title={membershipLoadError}
              description={NON_PIPELINE_BLOCKED_DESCRIPTION}
            />
          </div>
        )}
        {!membershipLoadError && nonPipelineActionsError && (
          <div className='mt-2 mb-2'>
            <AlertFloating
              color='error'
              title={NON_PIPELINE_SAVE_ERROR_TITLE}
              description={nonPipelineActionsError}
            />
          </div>
        )}
        <div className='mt-3'>
          <ButtonList aria-label={SUGGESTS_HEADING}>
            <ButtonList.Item
              trailing={
                <Toggle
                  isSelected={formData.non_pipeline_actions}
                  onChange={handleNonPipelineActionsToggle}
                  isDisabled={
                    !canWrite ||
                    nonPipelineActionsSaving ||
                    membershipLoading ||
                    !!membershipLoadError ||
                    membershipId === null
                  }
                  aria-label={NON_PIPELINE_LABEL}
                />
              }
            >
              <p className='mb-0.5 text-sm font-medium text-fg-primary'>
                {NON_PIPELINE_LABEL}
              </p>
              <p className='text-sm text-fg-tertiary'>
                {NON_PIPELINE_DESCRIPTION}
              </p>
              <p className='text-sm text-fg-tertiary'>{NON_PIPELINE_PRIVACY}</p>
            </ButtonList.Item>
          </ButtonList>
        </div>
      </div>
    </div>
  );
}
